import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Select,
  Input,
  InputNumber,
  Button,
  Collapse,
  Row,
  Col,
  Typography,
  Space,
  Switch,
  Tooltip,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Panel } = Collapse;
const { Text } = Typography;

const OPERATOR_OPTIONS = [
  { label: "Equal",                 value: "equal" },
  { label: "Not Equal",             value: "notEqual" },
  { label: "Greater Than",          value: "greaterThan" },
  { label: "Greater Than or Equal", value: "greaterThanInclusive" },
  { label: "Less Than",             value: "lessThan" },
  { label: "Less Than or Equal",    value: "lessThanInclusive" },
  { label: "In",                    value: "in" },
  { label: "Contains",              value: "contains" },
];

// Operators that make sense for each type
const OPERATORS_BY_TYPE = {
  number:  ["equal", "notEqual", "greaterThan", "greaterThanInclusive", "lessThan", "lessThanInclusive"],
  boolean: ["equal", "notEqual"],
  select:  ["equal", "notEqual", "in"],
  text:    ["equal", "notEqual", "contains"],
  date:    ["equal", "greaterThan", "greaterThanInclusive", "lessThan", "lessThanInclusive"],
};

const DEFAULT_FACTS = [
  { value: "amount",    label: "Amount",    type: "number",  description: "Payment amount in ₹" },
  { value: "branch",    label: "Branch",    type: "text",    description: "Branch name" },
  { value: "isCash",    label: "Is Cash",   type: "boolean", description: "True when payment mode is CASH", options: ["true", "false"] },
  { value: "isPledge",  label: "Is Pledge", type: "boolean", description: "True for pledge payments",       options: ["true", "false"] },
];

const uid = () => Math.random().toString(36).slice(2, 10);

// Remove _uid from all condition nodes before storing in form state / sending to API
const stripUids = (node) => {
  if (!node || typeof node !== "object") return node;
  const type = node.all !== undefined ? "all" : node.any !== undefined ? "any" : null;
  if (type) return { [type]: node[type].map(stripUids) };
  const { _uid, ...rest } = node;
  return rest;
};

const newCondition = (defaultFact = "amount") => ({
  _uid: uid(),
  fact: defaultFact,
  operator: "equal",
  value: "",
});

const newGroup = (defaultFact = "amount", type = "all") => ({
  [type]: [newCondition(defaultFact)],
});

// ── Value input: smart by fact type ─────────────────────────────────────────

function ValueInput({ fact, factMeta, value, onChange }) {
  if (!factMeta) {
    return (
      <Input
        value={value}
        placeholder="Value"
        style={{ width: 160 }}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (factMeta.type === "boolean") {
    // Store actual booleans (true/false), not strings
    const displayValue = value === true ? "true" : value === false ? "false" : undefined;
    return (
      <Select
        value={displayValue}
        placeholder="Select value"
        style={{ width: 160 }}
        allowClear
        onChange={(v) => onChange(v === "true" ? true : v === "false" ? false : "")}
        options={[
          { label: "True", value: "true" },
          { label: "False", value: "false" },
        ]}
      />
    );
  }

  if (factMeta.options && factMeta.options.length <= 5) {
    const opts = factMeta.options || [];
    return (
      <Select
        value={value === "" ? undefined : value}
        placeholder="Select value"
        style={{ width: 160 }}
        allowClear
        onChange={(v) => onChange(v === undefined ? "" : v)}
        options={opts.map((o) => ({ label: String(o), value: o }))}
      />
    );
  }

  if (factMeta.type === "number") {
    return (
      <InputNumber
        value={value === "" ? undefined : value}
        placeholder="e.g. 20000"
        style={{ width: 160 }}
        onChange={(v) => onChange(v === null ? "" : v)}
      />
    );
  }

  return (
    <Input
      value={value}
      placeholder="Value"
      style={{ width: 160 }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ── Condition row ────────────────────────────────────────────────────────────

function ConditionRow({ cond, facts, onUpdate, onRemove, removable }) {
  const factMeta = facts.find((f) => f.value === cond.fact);
  const allowedOps = factMeta ? (OPERATORS_BY_TYPE[factMeta.type] || OPERATOR_OPTIONS.map((o) => o.value)) : OPERATOR_OPTIONS.map((o) => o.value);
  const opOptions = OPERATOR_OPTIONS.filter((o) => allowedOps.includes(o.value));

  const handleFactChange = (val) => {
    const newMeta = facts.find((f) => f.value === val);
    const defaultOp = newMeta ? (OPERATORS_BY_TYPE[newMeta.type]?.[0] ?? "equal") : "equal";
    onUpdate({ ...cond, fact: val, operator: defaultOp, value: "" });
  };

  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8, flexWrap: "nowrap" }}>
      {/* Fact selector */}
      <Col>
        <Select
          value={cond.fact || undefined}
          placeholder="Select fact"
          style={{ width: 200 }}
          optionLabelProp="title"
          onChange={handleFactChange}
          dropdownStyle={{ minWidth: 280 }}
        >
          {facts.map((f) => (
            <Select.Option key={f.value} value={f.value} title={f.label}>
              <div style={{ lineHeight: 1.4, padding: "2px 0" }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{f.label}</div>
                {f.description && (
                  <div style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "normal" }}>
                    {f.description}
                  </div>
                )}
              </div>
            </Select.Option>
          ))}
        </Select>
      </Col>

      {/* Operator selector */}
      <Col>
        <Select
          value={cond.operator}
          style={{ width: 180 }}
          options={opOptions}
          onChange={(val) => onUpdate({ ...cond, operator: val })}
        />
      </Col>

      {/* Value input */}
      <Col>
        <ValueInput
          fact={cond.fact}
          factMeta={factMeta}
          value={cond.value}
          onChange={(val) => onUpdate({ ...cond, value: val })}
        />
      </Col>

      {/* Remove */}
      <Col>
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          disabled={!removable}
        />
      </Col>
    </Row>
  );
}

// ── Recursive condition tree ─────────────────────────────────────────────────

function ConditionTree({ group, onChange, removable = false, onRemove, facts }) {
  const groupType = Object.keys(group)[0]; // "all" or "any"
  const conditions = group[groupType] || [];
  const defaultFact = facts[0]?.value || "amount";

  const handleAddCondition = () => {
    onChange({ [groupType]: [...conditions, newCondition(defaultFact)] });
  };

  const handleAddGroup = () => {
    onChange({ [groupType]: [...conditions, newGroup(defaultFact, "all")] });
  };

  const handleRemoveItem = (idx) => {
    if (conditions.length === 1) return;
    onChange({ [groupType]: conditions.filter((_, i) => i !== idx) });
  };

  const handleUpdateItem = (idx, updated) => {
    onChange({ [groupType]: conditions.map((item, i) => (i === idx ? updated : item)) });
  };

  const handleSwitchType = (val) => {
    onChange({ [val]: conditions });
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, background: "#fafcff", border: "1px solid #d9e8ff" }}
      bodyStyle={{ paddingBottom: 8 }}
      title={
        <Space>
          <ApartmentOutlined style={{ color: "#1677ff" }} />
          <Select
            size="small"
            value={groupType}
            style={{ width: 120 }}
            onChange={handleSwitchType}
            options={[
              { value: "all", label: "ALL  (AND)" },
              { value: "any", label: "ANY  (OR)" },
            ]}
          />
          {removable && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onRemove}>
              Remove Group
            </Button>
          )}
        </Space>
      }
    >
      {conditions.map((cond, idx) => {
        const isGroup = cond.all !== undefined || cond.any !== undefined;
        const rowKey = isGroup ? `group_${idx}` : `${cond._uid || cond.fact}_${idx}`;

        return isGroup ? (
          <ConditionTree
            key={rowKey}
            group={cond}
            onChange={(val) => handleUpdateItem(idx, val)}
            removable
            onRemove={() => handleRemoveItem(idx)}
            facts={facts}
          />
        ) : (
          <ConditionRow
            key={rowKey}
            cond={cond}
            facts={facts}
            onUpdate={(val) => handleUpdateItem(idx, val)}
            onRemove={() => handleRemoveItem(idx)}
            removable={conditions.length > 1}
          />
        );
      })}

      <Space style={{ marginTop: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={handleAddCondition}>
          Add Condition
        </Button>
        <Button size="small" icon={<PlusOutlined />} onClick={handleAddGroup}>
          Add Group
        </Button>
      </Space>
    </Card>
  );
}

// ── Main RuleBuilderForm ─────────────────────────────────────────────────────

const RuleBuilderForm = ({ value = {}, onChange, factOptions = [] }) => {
  const [mode, setMode] = useState("visual");
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState("");

  // Use ref to avoid stale-closure issues with onChange in effects
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const facts = factOptions.length > 0 ? factOptions : DEFAULT_FACTS;
  const defaultFact = facts[0]?.value || "amount";

  // Initialize default rule structure once (only when conditions are absent)
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && (!value || !value.conditions)) {
      initialized.current = true;
      onChangeRef.current?.({
        conditions: newGroup(defaultFact, "all"),
        event: { type: "template-match", params: { message: "" } },
      });
    }
  }, [defaultFact]);

  // Sync JSON editor when switching to JSON mode
  useEffect(() => {
    if (mode === "json") {
      setJsonInput(JSON.stringify(value, null, 2));
    }
  }, [mode]);

  const handleGroupChange = (group) => {
    const updated = {
      conditions: stripUids(group),
      event: value?.event || { type: "template-match", params: { message: "" } },
    };
    onChange?.(updated);
    if (mode === "json") setJsonInput(JSON.stringify(updated, null, 2));
  };

  const handleEventParamChange = (e) => {
    const updated = {
      ...value,
      event: { ...value?.event, params: { ...value?.event?.params, message: e.target.value } },
    };
    onChange?.(updated);
    if (mode === "json") setJsonInput(JSON.stringify(updated, null, 2));
  };

  const handleJsonChange = (e) => {
    const val = e.target.value;
    setJsonInput(val);
    try {
      const parsed = JSON.parse(val);
      onChange?.(parsed);
      setJsonError("");
    } catch {
      setJsonError("Invalid JSON");
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <ApartmentOutlined />
          Rule Builder
          <Tooltip title="Switch between visual builder and JSON editor">
            <Switch
              checkedChildren={<EditOutlined />}
              unCheckedChildren={<ApartmentOutlined />}
              checked={mode === "json"}
              onChange={(checked) => setMode(checked ? "json" : "visual")}
              style={{ marginLeft: 16 }}
            />
          </Tooltip>
        </Space>
      }
    >
      {mode === "visual" ? (
        <>
          {value?.conditions ? (
            <ConditionTree
              group={value.conditions}
              onChange={handleGroupChange}
              facts={facts}
            />
          ) : (
            <Text type="secondary">Loading rule builder…</Text>
          )}

          <Collapse style={{ marginBottom: 12 }}>
            <Panel header="Event Params (optional)" key="event">
              <Input
                placeholder="Event message label"
                value={value?.event?.params?.message || ""}
                onChange={handleEventParamChange}
                style={{ width: 400 }}
              />
            </Panel>
          </Collapse>
        </>
      ) : (
        <Collapse defaultActiveKey={["json"]}>
          <Panel header="Edit Rule JSON" key="json">
            <Input.TextArea
              rows={12}
              value={jsonInput}
              onChange={handleJsonChange}
              style={{ fontFamily: "monospace" }}
            />
            {jsonError && <div style={{ color: "red", marginTop: 4 }}>{jsonError}</div>}
          </Panel>
        </Collapse>
      )}

      <Text type="secondary" style={{ fontSize: 12 }}>
        Use nested groups for complex AND / OR logic.
      </Text>
    </Card>
  );
};

export default RuleBuilderForm;
